import React, { useState, useEffect } from 'react';
import QRCodeService from '../../services/QRCodeService';

/**
 * QRModal Component
 *
 * Reusable modal for displaying QR codes and share links for addresses
 */
const QRModal = ({
  isOpen,
  onClose,
  address,
  name = '',
  title = 'Share Address'
}) => {
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState('');

  useEffect(() => {
    if (isOpen && address) {
      generateQRAndUrl();
    }
  }, [isOpen, address, name]);

  const generateQRAndUrl = async () => {
    try {
      setLoading(true);
      setError('');

      // Generate QR code and shareable URL in parallel
      const [qrData, url] = await Promise.all([
        QRCodeService.generateAddressQR(address, name),
        QRCodeService.generateShareableURL(address, name)
      ]);

      setQrDataUrl(qrData);
      setShareUrl(url);
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError('Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = async () => {
    const success = await QRCodeService.copyToClipboard(shareUrl);
    if (success) {
      setCopySuccess('Link copied!');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  const handleCopyAddress = async () => {
    const success = await QRCodeService.copyToClipboard(address);
    if (success) {
      setCopySuccess('Address copied!');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  const handleDownloadQR = () => {
    if (qrDataUrl) {
      const filename = `${name || 'wallet'}-qr-code.png`;
      QRCodeService.downloadDataUrl(qrDataUrl, filename);
    }
  };

  const handleClose = () => {
    setQrDataUrl(null);
    setShareUrl('');
    setError('');
    setCopySuccess('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Address Info */}
          <div className="text-center">
            <div className="font-medium text-gray-900 mb-1">
              {name || `Wallet ${address?.slice(0, 6)}...${address?.slice(-4)}`}
            </div>
            <div className="text-xs text-gray-600 font-mono break-all bg-gray-50 p-2 rounded">
              {address}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Success Message */}
          {copySuccess && (
            <div className="bg-green-50 border border-green-200 rounded p-3 text-green-700 text-sm">
              {copySuccess}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Generating QR code...</p>
            </div>
          )}

          {/* QR Code */}
          {!loading && qrDataUrl && (
            <div className="text-center">
              <div className="bg-white p-4 rounded-lg border border-gray-200 inline-block">
                <img
                  src={qrDataUrl}
                  alt="Address QR Code"
                  className="w-48 h-48 mx-auto"
                />
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Scan this QR code to add address to wallet
              </p>
            </div>
          )}

          {/* Shareable URL */}
          {!loading && shareUrl && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shareable Link
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 font-mono"
                />
                <button
                  onClick={handleCopyUrl}
                  className="px-3 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 transition-colors"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Share this link for others to easily add your address
              </p>
            </div>
          )}

          {/* Action Buttons */}
          {!loading && qrDataUrl && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleDownloadQR}
                className="flex items-center justify-center px-4 py-2 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download QR
              </button>
              <button
                onClick={handleCopyAddress}
                className="flex items-center justify-center px-4 py-2 bg-green-500 text-white text-sm rounded-md hover:bg-green-600 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy Address
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClose}
            className="w-full px-4 py-2 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRModal;